import { Injectable } from '@angular/core';
import {
  ApiService,
  GetRequestArguments,
  PostRequestArguments,
  Refresh,
} from './api.service';
import { HttpParams } from '@angular/common/http';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { UntypedFormBuilder, Validators, FormGroup } from '@angular/forms';
import { FeedbackService } from './feedback.service';
import { NavController } from '@ionic/angular';
import { CacheService } from 'ionic-cache';

export interface Holiday {
  is: number;
  should: number;
}

@Injectable({
  providedIn: 'root',
})
export class TimesheetService {
  public $range = new BehaviorSubject([
    new Date(new Date().getFullYear(), new Date().getMonth(), 2)
      .toISOString()
      .split('T')[0],
    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1)
      .toISOString()
      .split('T')[0],
  ]);
  public $timesheet = new BehaviorSubject([]);
  public $calendarEntries = new BehaviorSubject([]);
  public $summary = new BehaviorSubject([]);
  public $summarySelect = new BehaviorSubject([]);
  public $editing = new BehaviorSubject({});
  public $new = new BehaviorSubject({});
  public $unitTypes = new BehaviorSubject([]);

  public $review = new BehaviorSubject([]);
  public $itemCategories = new BehaviorSubject([]);
  public $itemStates = new BehaviorSubject([]);
  public $countries = new BehaviorSubject([]);
  public $tenantCountry = new BehaviorSubject([]);
  public $filterForm = new BehaviorSubject(undefined);
  public $reviewing = new BehaviorSubject([]);

  public $holidays = new BehaviorSubject<Holiday>(undefined);

  public $editingItemCategory = new BehaviorSubject({});
  public $newItemCategory = new BehaviorSubject({});

  private url = 'timesheet';
  private reviewUrl = 'timesheet/review';
  private createReportUrl = 'timesheet/report';
  private summaryUrl = 'timesheet/summary';

  private filteredUrl = 'timesheet/filter';

  private groupKey = 'timesheet';
  private hour = 60 * 60;

  timeForm: FormGroup;

  constructor(
    private api: ApiService,
    private formBuilder: UntypedFormBuilder,
    private feedbackService: FeedbackService,
    private navController: NavController,
    private cache: CacheService
  ) {
    this.createTimeForm();
    this.api.updateObservable(this.createFilterForm(), this.$filterForm);
  }

  public load(refresh: Refresh = 'all', range?: BehaviorSubject<string[]>, calendar: boolean = false) {
    const args = new GetRequestArguments({
      url: this.url,
      cacheGroup: this.groupKey,
      refresh,
      ttl: 20,
      params: new HttpParams()
        .set('from', range ? range.value[0] : this.$range.value[0])
        .set('to', range ? range.value[1] : this.$range.value[1]),
    });
    const request = this.api.cachedGetRequest(args);
    request.subscribe(
      (timesheet) => {
        this.api.updateObservable(timesheet, calendar ? this.$calendarEntries : this.$timesheet);
      },
      (error) => {
        this.feedbackService.invalidScopes(error);
      }
    );
    return request;
  }

  loadSummary(refresh: Refresh = 'all') {
    const args = new GetRequestArguments({
      url: [this.url, 'summary'],
      cacheGroup: this.groupKey,
      ttl: 20,
      refresh,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(
      (summary) => {
        this.api.updateObservable(summary, this.$summary);
      },
      (error) => {
        this.feedbackService.invalidScopes(error);
      }
    );
    return call;
  }

  loadMonthSummary(refresh: Refresh = 'all') {
    const args = new GetRequestArguments({
      url: [this.url, 'summary-select'],
      cacheGroup: this.groupKey,
      ttl: 20,
      refresh,
      params: new HttpParams()
        .set('from', this.$range.value[0])
        .set('to', this.$range.value[1]),
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe(
      (summary) => {
        this.api.updateObservable(summary, this.$summarySelect);
      },
      (error) => {
        this.feedbackService.invalidScopes(error);
      }
    );
    return call;
  }

  public loadAll(refresh: Refresh = 'expired') {
    return new Promise<void>((resolve, reject) => {
      forkJoin([
        this.loadItemCategories(refresh),
        this.loadItemStates(refresh),
        this.loadCountries(refresh),
      ]).subscribe(
        () => {
          resolve();
        },
        (error) => {
          reject();
        }
      );
    });
  }

  public loadReview(refresh: Refresh = 'expired') {
    let params = new HttpParams();
    if (!this.$filterForm.value.value) {
      return of(false);
    }
    for (const key of Object.keys(this.$filterForm.value.value)) {
      if (this.$filterForm.value.value[key] instanceof Array) {
        if (this.$filterForm.value.value[key].length < 1) {
          return of(false);
        }
        params = params.set(
          key,
          this.$filterForm.value.value[key].map((entry) => entry.id).join(',')
        );
      } else {
        params = params.set(key, this.$filterForm.value.value[key]);
      }
    }
    const args = new GetRequestArguments({
      url: [this.url, 'filter'],
      cacheGroup: this.groupKey,
      refresh,
      params,
    });
    const request = this.api.cachedGetRequest(args);
    request.subscribe((timesheet) => {
      this.api.updateObservable(timesheet, this.$review);
    });

    return request;
  }

  public loadForReview(userId, refresh: Refresh = 'expired') {
    this.$filterForm.subscribe((form) => {
      let params = new HttpParams();
      if (!form.value) {
        this.navController.navigateBack(['timesheet', 'review']);
      }
      for (const key of Object.keys(form.value)) {
        if (form.value[key] instanceof Array) {
          if (form.value[key].length < 1) {
            this.navController.navigateBack(['timesheet', 'review']);
            break;
          }
          params = params.set(
            key,
            form.value[key].map((entry) => entry.id).join(',')
          );
        } else {
          if (form.value[key] === null || form.value[key] === undefined) {
            this.navController.navigateBack(['timesheet', 'review']);
            break;
          }
          params = params.set(key, form.value[key]);
        }
      }
      const args = new GetRequestArguments({
        url: [this.url, 'filter', userId],
        cacheGroup: this.groupKey,
        refresh,
        params,
      });
      const request = this.api.cachedGetRequest(args);
      request.subscribe((timesheet) => {
        this.$reviewing.next(timesheet);
      });
    });
  }

  public post(time: FormData) {
    const args = new PostRequestArguments({
      url: this.url,
      object: time,
    });
    const call = this.api.postRequest(args);
    call.subscribe({
      next: (times) => {
        this.$editing.next(undefined);
        this.api.updateObservable(times, this.$timesheet);
        this.api.getCacheUrl(this.url).then((url) => {
          this.cache.saveItem(url, times, this.groupKey, 60 * 60);
        });
      },
      error: (error) => {
        console.log(error);
      },
    });
    return call;
  }

  public delete(timeId) {
    const args = new PostRequestArguments({
      url: this.url,
      object: timeId,
    });
    const call = this.api.deleteRequest(args);
    call.subscribe({
      next: (id) => {
        if (typeof id === 'string') {
          const index = this.$timesheet.value.findIndex(
            (c) => c.id === parseInt(id, 10)
          );
          this.$timesheet.value.splice(index, 1);
          this.api.getCacheUrl(this.url).then((url) => {
            this.cache.saveItem(
              url,
              this.$timesheet.value,
              this.groupKey,
              60 * 60
            );
          });
        }
        this.feedbackService.message(
          'Timesheet entry was successfully deleted',
          'Success'
        );
      },
      error: (error) => {
        this.feedbackService.standardError(error);
      },
    });
    return call;
  }

  public loadTimeForEdit(timeId) {
    const args = new GetRequestArguments({
      url: [this.url, timeId],
      cacheGroup: undefined,
    });
    this.api.cachedGetRequest(args).subscribe(
      (time) => {
        const form = this.createTimeForm();
        this.fillTimeForm(form, time);
        this.api.updateObservable(form, this.$editing);
      },
      (error) => {
        this.feedbackService.standardError(error, 4000, 'top').then(() => {
          this.navController.navigateBack(['/', 'timesheet']);
        });
      }
    );
  }

  public createTimeForm() {
    this.timeForm = this.formBuilder.group({
      item_category: [undefined, [Validators.required]],
      item_category_id: [undefined, []],
      created_at: [undefined, []],
      date: [[], [Validators.required]],
      deleted_at: [undefined, []],
      hours: [undefined, [Validators.required]],
      id: [undefined, []],
      minutes: [undefined, [Validators.required]],
      project: [{ value: '', disabled: false }, [Validators.required]],
      project_id: [undefined, []],
      remote: [false, [Validators.required]],
      description: [undefined, [Validators.maxLength(255)]],
      item_state: [undefined, []],
      item_state_id: [undefined, []],
      updated_at: [undefined, []],
      user_id: [undefined, []],
      country_id: [undefined, []],
      country: [undefined, []],
      id_activity_detail: [undefined, []],
      quantity: [0, [Validators.required]],
      employment: [undefined, []],
      tracking_target: [undefined, []],
      approved_at: [undefined, []],
      submitted_at: [undefined, []],
    });

    //set Validation based on project tracking target
    this.timeForm.get('project').valueChanges.subscribe((value) => {
      this.timeForm.controls.tracking_target.patchValue(value.tracking_target);
    });

    //set Validation based on emloyment weekly hours divided by 5
    this.timeForm.get('employment').valueChanges.subscribe((value) => {
      this.timeForm.controls.tracking_target.patchValue(
        value?.weekly_hours / 5
      );
    });

    return this.timeForm;
  }

  public fillTimeForm(form, time) {
    if (time.id) {
      form.controls.item_category.patchValue(time.item_category);
      form.controls.item_category_id.patchValue(time.item_category_id);
      form.controls.created_at.patchValue(time.created_at);
      form.controls.date.patchValue(time.date);
      form.controls.deleted_at.patchValue(time.deleted_at);
      form.controls.id.patchValue(time.id);
      form.controls.hours.patchValue(
        time.activity_details ? time.activity_details.hours : 0
      );
      form.controls.minutes.patchValue(
        time.activity_details ? time.activity_details.minutes : 0
      );
      form.controls.project.patchValue(time.project);
      form.controls.project_id.patchValue(time.project_id);
      form.controls.remote.patchValue(time.remote == '1');
      form.controls.description.patchValue(time.description);
      form.controls.item_state.patchValue(time.item_state);
      form.controls.item_state_id.patchValue(time.item_state_id);
      form.controls.updated_at.patchValue(time.updated_at);
      form.controls.country_id.patchValue(time.country_id);
      form.controls.country.patchValue(
        this.$countries.value.find((c) => c.id === time.country_id)
      );

      form.controls.user_id.patchValue(time.user_id);
      form.controls.quantity.patchValue(time.quantity);
      form.controls.approved_at.patchValue(time.approved_at);
      form.controls.submitted_at.patchValue(time.submitted_at);
    }
  }

  public createTimeFormData(time) {
    const formData = this.api.createFormData(time);
    formData.append('from', this.$range.value[0]);
    formData.append('to', this.$range.value[1]);
    return formData;
  }

  public createFilterForm() {
    return this.formBuilder.group({
      itemCategories: [undefined, [Validators.required]],
      clients: [undefined, [Validators.required]],
      from: [
        new Date(new Date().getFullYear(), new Date().getMonth(), 2)
          .toISOString()
          .split('T')[0],
        [Validators.required],
      ],
      projects: [undefined, [Validators.required]],
      itemStates: [undefined, [Validators.required]],
      to: [
        new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1)
          .toISOString()
          .split('T')[0],
        [Validators.required],
      ],
      users: [undefined, [Validators.required]],
    });
  }

  getFiltered(filter, refresh: Refresh = 'expired', userId: number = null) {
    let params = new HttpParams();
    for (const key of Object.keys(filter)) {
      params = params.set(
        key,
        filter[key].map((entry) => entry.id).join(',')
      );
    }
    const args = new GetRequestArguments({
      url: userId ? this.filteredUrl + '/' + userId : this.filteredUrl,
      cacheGroup: this.groupKey,
      refresh,
      params,
    });
    return this.api.cachedGetRequest(args);
  }

  loadItemCategories(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: [this.url, 'itemCategories'],
      cacheGroup: this.groupKey,
      refresh,
      ttl: this.hour * 24,
      delayed: false,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe((itemCategories) => {
      this.api.updateObservable(itemCategories, this.$itemCategories);
    });
    return call;
  }

  loadItemStates(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: [this.url, 'itemStates'],
      cacheGroup: this.groupKey,
      refresh,
      ttl: this.hour * 24,
      delayed: false,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe((itemStates) => {
      this.api.updateObservable(itemStates, this.$itemStates);
    });
    return call;
  }

  loadCountries(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: ['countries'],
      cacheGroup: this.groupKey,
      refresh,
      ttl: this.hour * 24,
      delayed: false,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe((countries) => {
      this.api.updateObservable(
        countries.find((c) => c.is_tenant_country === true),
        this.$tenantCountry
      );
      this.api.updateObservable(countries, this.$countries);
    });
    return call;
  }

  getForUser(fromDate, toDate, userId, refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: this.reviewUrl + '/' + userId,
      cacheGroup: this.groupKey,
      params: new HttpParams().set('from', fromDate).set('to', toDate),
      refresh,
    });
    return this.api.cachedGetRequest(args);
  }

  deleteTimesheet(timesheet) {
    const args = new PostRequestArguments({
      url: this.url,
      object: timesheet,
    });
    const call = this.api.deleteRequest(args);
    call.subscribe(
      (id) => {
        if (typeof id === 'string') {
          const index = this.$timesheet.value.findIndex(
            (c) => c.id === parseInt(id, 10)
          );
          this.$timesheet.value.splice(index, 1);
        }
        this.feedbackService.message(
          'Timesheet was successfully deleted',
          'Success'
        );
      },
      (error) => {
        this.feedbackService.standardError(error);
      }
    );
    return call;
  }

  updateReview(timesheet: FormData) {
    const args = new PostRequestArguments({
      url: this.reviewUrl,
      object: timesheet,
    });
    return this.api.postRequest(args);
  }

  createReport(fromDate, toDate) {
    const args = new GetRequestArguments({
      url: this.createReportUrl,
      cacheGroup: undefined,
      params: new HttpParams().set('from', fromDate).set('to', toDate),
    });
    return this.api.cachedGetRequest(args);
  }

  summary(
    year: number = new Date().getFullYear(),
    refresh: Refresh = 'expired'
  ) {
    const args = new GetRequestArguments({
      url: this.summaryUrl,
      cacheGroup: this.groupKey,
      params: new HttpParams().set('year', year),
      refresh,
    });
    return this.api.cachedGetRequest(args);
  }

  swipe(time, transition_name) {
    const args = new GetRequestArguments({
      url: [this.url, time.id, 'transition', transition_name],
      cacheGroup: undefined,
    });
    const request = this.api.cachedGetRequest(args);
    request.subscribe(
      (data) => {
        this.loadForReview(this.$reviewing.value[0]?.user_id, 'all');
        this.loadReview('all');
      },
      (error) => {
        console.log('error:');
        console.log(error);
      }
    );
    return request;
  }

  swipeMultiple(timeIds, fromState, transition_name) {
    let params = new HttpParams();
    params = params.append('timeIds', timeIds.join(','));

    const args = new GetRequestArguments({
      url: [this.url, 'multi_transition', fromState, transition_name],
      cacheGroup: undefined,
      params,
    });
    const request = this.api.cachedGetRequest(args);
    request.subscribe(() => {
      this.loadForReview(this.$reviewing.value[0]?.user_id, 'all');
      this.loadReview('all');
    });
    return request;
  }

  getHolidays(year: number, refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: [this.url, 'holidays', year],
      cacheGroup: this.groupKey,
      ttl: this.hour * 24,
      refresh,
    });
    const request = this.api.cachedGetRequest(args);
    request.subscribe((res) => {
      this.api.updateObservable(res, this.$holidays);
    });
    return request;
  }

  public createItemCategoryForm() {
    return this.formBuilder.group({
      id: [NaN, []],
      name: [undefined, []],
      description: [undefined, []],
      billable: [false, []],
      created_at: [undefined, []],
      updated_at: [undefined, []],
      deleted_at: [undefined, []],
      product_type_id: [undefined, []],
      unit_id: [undefined, []],
      active: [0, []],
      display_label: [undefined, []],
      email_notifications: [false, []],
    });
  }

  public fillItemCategoryForm(form, item_category) {
    if (item_category !== undefined && item_category !== null) {
      form.controls.id.patchValue(parseInt(item_category.id, 10));
      form.controls.name.patchValue(item_category.name);
      form.controls.description.patchValue(item_category.description);
      form.controls.billable.patchValue(Boolean(item_category.billable));
      form.controls.created_at.patchValue(item_category.created_at);
      form.controls.deleted_at.patchValue(item_category.deleted_at);
      form.controls.updated_at.patchValue(item_category.updated_at);
      form.controls.product_type_id.patchValue(item_category.product_type_id);
      form.controls.unit_id.patchValue(item_category.unit_id);
      form.controls.active.patchValue(item_category.active);
      form.controls.display_label.patchValue(item_category.display_label);
      item_category.email_notifications = Boolean(
        item_category.email_notifications
      );
      form.controls.email_notifications.patchValue(
        item_category.email_notifications
      );
    }
  }

  public loadItemCategory(itemCategoryId) {
    const args = new GetRequestArguments({
      url: [this.url, 'itemCategories', itemCategoryId],
      cacheGroup: undefined,
    });
    this.api.cachedGetRequest(args).subscribe(
      (itemCategory) => {
        const form = this.createItemCategoryForm();
        this.fillItemCategoryForm(form, itemCategory);
        this.api.updateObservable(form, this.$editingItemCategory);
      },
      (error) => {
        this.feedbackService.standardError(error, 4000, 'top').then(() => {
          this.navController.navigateBack([
            '/',
            'customization',
            'projectActivities',
          ]);
        });
      }
    );
  }

  public postItemCategory(itemCategory) {
    const args = new PostRequestArguments({
      url: [this.url, 'itemCategories'],
      object: itemCategory,
    });
    const call = this.api.postRequest(args);
    call.subscribe(
      (itemCategories) => {
        this.$editingItemCategory.next(undefined);
        this.api.updateObservable(itemCategories, this.$itemCategories);
        this.api.getCacheUrl(this.url).then((url) => {
          this.cache.saveItem(url, itemCategories, this.groupKey, 60 * 60 * 24);
        });
      },
      (error) => {
        this.feedbackService.standardError(error);
      }
    );
    return call;
  }

  public createItemCategoryFormData(item_category) {
    return this.api.createFormData(item_category);
  }

  public deleteItemCategory(itemCategoryId) {
    const args = new PostRequestArguments({
      url: [this.url, 'itemCategories'],
      object: itemCategoryId,
    });
    const call = this.api.deleteRequest(args);
    call.subscribe(
      (id) => {
        if (typeof id === 'string') {
          const index = this.$itemCategories.value.findIndex(
            (c) => c.id === parseInt(id)
          );
          this.$itemCategories.value.splice(index, 1);
        }
        this.feedbackService.message(
          'Project Activity was successfully deleted',
          'Success'
        );
      },
      (error) => {
        this.feedbackService.standardError(error);
      }
    );
    return call;
  }

  public loadUnitTypes(refresh: Refresh = 'expired') {
    const args = new GetRequestArguments({
      url: ['unitTypes'],
      cacheGroup: this.groupKey,
      ttl: this.hour * 24,
      refresh,
    });
    const call = this.api.cachedGetRequest(args);
    call.subscribe((UnitTypes) => {
      this.api.updateObservable(UnitTypes, this.$unitTypes);
    });
    return call;
  }
}
